import React, { useState, useEffect } from "react";
import { Dropdown, Menu, Drawer } from "antd";
import Logo from "../assets/img/newlogo.png";
import ChatBubble from "../assets/img/chat_bubble.png";
import { useNavigate, useLocation } from "react-router-dom";
import secureLocalStorage from "react-secure-storage";
import { getUserDetail, logout } from "../util/util";
import PageHeader from "../layouts/PageHeader";
import { useUnreadCount } from "../hooks/useUnreadCount";
import { Bell } from "lucide-react";
import NotificationsDrawer from "../components/NotificationsDrawer";
import MVestFlow from "../components/MvestPopUps";

export const DashboardLayout = (props) => {
  const location = useLocation();
  const [showMvest, setShowMvest] = useState(false);

  useEffect(() => {
    setShowMvest(false);
  }, [location.pathname]);

  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 900);
  const [sidebarExpanded, setSidebarExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  const [notificationsDrawerOpen, setNotificationsDrawerOpen] = useState(false);
  const { data: unreadCountData } = useUnreadCount();
  const unreadCount = unreadCountData?.data || 0;

  const handleSidebarNav = (path) => {
    navigate(path);
    if (window.innerWidth <= 900) setSidebarOpen(false);
  };

  React.useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth <= 900;
      setIsMobile(mobile);
      setSidebarOpen(!mobile);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const logoutUser = () => {
    logout();
    window.location.href = "/onboard/login";
  };

  return (
    <>
      <div>
        {/* Navbar - Fully Responsive */}
        <nav
          className="navbar bg-om-dark navbar-dark"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1200,
            height: "64px",
            minHeight: "64px",
          }}
        >
          <div
            className="container-fluid"
            style={{
              position: "relative",
              overflowX: "auto",
              whiteSpace: "nowrap",
              padding: "0 12px",
              height: "100%",
            }}
          >
            <div
              className="navbar-brand dashboard-navbar-brand"
              style={{
                display: "flex",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <img
                src={Logo}
                alt="Old Mutual"
                style={{
                  height: "clamp(28px, 8vw, 32px)",
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            </div>

            <style>{`
              @media (max-width: 900px) {
                .dashboard-navbar-brand {
                  margin-left: 0 !important;
                  padding-left: 0 !important;
                }
                .container-fluid {
                  padding-left: 12px !important;
                  padding-right: 12px !important;
                }
              }
              @media (min-width: 901px) {
                .dashboard-navbar-brand {
                  margin-left: 0;
                  padding-left: 0;
                }
              }
              @media (max-width: 480px) {
                .user-name-label {
                  display: none !important;
                }
                .btn-white {
                  padding: 4px 8px !important;
                }
              }
              @media (max-width: 360px) {
                .btn-white span:not(.bi) {
                  display: none !important;
                }
              }
            `}</style>

            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "clamp(4px, 3vw, 8px)",
                marginLeft: "auto",
              }}
            >
              <div style={{ position: "relative" }}>
                <button
                  onClick={() => setNotificationsDrawerOpen((prev) => !prev)}
                  aria-expanded={notificationsDrawerOpen}
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    position: "relative",
                    padding: "clamp(4px, 2vw, 8px)",
                    borderRadius: 8,
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Bell size={20} />
                  {unreadCount > 0 && (
                    <span
                      className="notification-count"
                      style={{
                        position: "absolute",
                        top: 0,
                        right: 0,
                        background: "#ff4757",
                        color: "#fff",
                        borderRadius: "50%",
                        width: "clamp(14px, 4vw, 16px)",
                        height: "clamp(14px, 4vw, 16px)",
                        fontSize: "clamp(8px, 2.5vw, 10px)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: 600,
                      }}
                    >
                      {unreadCount > 99 ? "99+" : unreadCount}
                    </span>
                  )}
                </button>
              </div>

              <Dropdown
                overlay={
                  <Menu
                    style={{
                      borderRadius: 12,
                      minWidth: 160,
                      boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
                    }}
                  >
                    <Menu.Item
                      key="settings"
                      onClick={() => navigate("/dashboard/settings")}
                      style={{ fontWeight: 600 }}
                    >
                      <i className="bi bi-gear me-2" /> Settings
                    </Menu.Item>
                    <Menu.Divider />
                    <Menu.Item
                      key="logout"
                      onClick={logoutUser}
                      style={{ fontWeight: 600 }}
                    >
                      <i className="bi bi-box-arrow-right me-2" /> Logout
                    </Menu.Item>
                  </Menu>
                }
                trigger={["click"]}
                placement="bottomRight"
              >
                <button
                  className="btn btn-white"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "clamp(4px, 2vw, 8px) clamp(8px, 3vw, 12px)",
                    fontSize: "clamp(12px, 3.5vw, 14px)",
                    whiteSpace: "nowrap",
                  }}
                >
                  <i
                    className="bi bi-person-circle"
                    style={{ fontSize: "clamp(14px, 4vw, 15px)" }}
                  />
                  <span
                    className="ms-1 user-name-label"
                    style={{ marginLeft: "4px" }}
                  >
                    {getUserDetail()?.name}
                  </span>
                  <i
                    className="bi bi-caret-down ms-2"
                    style={{
                      fontSize: "clamp(10px, 3vw, 12px)",
                      marginLeft: "4px",
                    }}
                  />
                </button>
              </Dropdown>
            </div>
          </div>
        </nav>

        {/* Spacer */}
        <div style={{ height: "64px", width: "100%", background: "#181526" }} />

        <div
          style={{
            display: "flex",
            width: "100%",
            minHeight: "calc(100vh - 64px)",
            overflow: "visible",
            paddingLeft: 0,
            paddingRight: 0,
          }}
        >
          {/* Desktop Sidebar */}
          {!isMobile && sidebarOpen && (
            <CustomSidebar
              navigate={handleSidebarNav}
              expanded={sidebarExpanded}
              setExpanded={setSidebarExpanded}
            />
          )}

          {/* Main Content */}
          <div
            style={{
              flexGrow: 1,
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              overflow: "visible",
              position: "relative",
              transition: "margin-left 0.2s ease, max-width 0.2s ease",
              maxWidth: !isMobile && sidebarOpen
                ? `calc(100% - ${sidebarExpanded ? 280 : 100}px)`
                : "100%",
              marginLeft: !isMobile && sidebarOpen
                ? sidebarExpanded
                  ? "280px"
                  : "100px"
                : "0",
            }}
          >
            <PageHeader />
            <div
              className="omk-bg-secondary padding-general-mobile dashboard-content-area"
              style={{
                flexGrow: 1,
                minWidth: 0,
                overflow: "visible",
                paddingBottom: isMobile ? "70px" : "20px",
                paddingLeft: 0,
                paddingRight: 0,
                width: "100%",
              }}
            >
              {props.children}
            </div>
            {/* Mobile bottom nav */}
            {isMobile && <MobileBottomNav navigate={handleSidebarNav} />}
          </div>
        </div>
      </div>

      <Drawer
        title="Notifications"
        placement="right"
        onClose={() => setNotificationsDrawerOpen(false)}
        open={notificationsDrawerOpen}
        width={window.innerWidth <= 480 ? "100%" : 360}
        closeIcon={<span style={{ fontSize: 20, color: "#000" }}>×</span>}
        bodyStyle={{
          padding: 0,
          paddingBottom: isMobile ? 80 : 0,
          overflowY: "auto",
        }}
        headerStyle={{ padding: "12px 16px" }}
        drawerStyle={{ zIndex: 4000 }}
      >
        <NotificationsDrawer
          onClose={() => setNotificationsDrawerOpen(false)}
        />
      </Drawer>
      <MVestFlow visible={showMvest} onClose={() => setShowMvest(false)} />
    </>
  );
};

// Custom Sidebar component - Fully Responsive
function CustomSidebar({ navigate, expanded, setExpanded }) {
  const [hoveredIndex, setHoveredIndex] = React.useState(null);
  const menuItems = [
    { label: "Home", icon: "home", path: "/dashboard" },
    { label: "Products", icon: "diamond", path: "/dashboard/products" },
    { label: "Transactions", icon: "receipt", path: "/dashboard/transactions" },
    { label: "Profile", icon: "person", path: "/dashboard/profile" },
    { label: "Settings", icon: "settings", path: "/dashboard/settings" },
    { label: "Feedback", path: "/dashboard/feedback" },
  ];
  const currentPath = window.location.pathname;

  return (
    <div
      style={{
        width: expanded ? "280px" : "100px",
        background: "#fff",
        borderRight: "1px solid #f0f0f0",
        height: "calc(100vh - 64px)",
        top: "64px",
        left: 0,
        position: "fixed",
        transition: "width 0.2s ease",
        display: "flex",
        flexDirection: "column",
        alignItems: expanded ? "flex-start" : "center",
        zIndex: 1100,
        overflowY: "auto",
        overflowX: "hidden",
      }}
      className="custom-sidebar shadow-sm"
    >
      {/* Logo */}
      <div
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: expanded ? "flex-start" : "center",
          padding: expanded ? "16px 18px 8px 18px" : "16px 0 8px 0",
          flexShrink: 0,
        }}
      >
        <img
          src={Logo}
          alt="OM Logo"
          style={{ width: "36px", height: "36px", borderRadius: "8px" }}
        />
      </div>

      {/* Toggle button */}
      <button
        onClick={() => setExpanded((e) => !e)}
        aria-label={expanded ? "Collapse sidebar" : "Expand sidebar"}
        style={{
          background: "none",
          border: "none",
          outline: "none",
          cursor: "pointer",
          margin: expanded ? "0 0 12px 18px" : "0 0 12px 0",
          padding: "8px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "24px",
          color: "#184b18",
          borderRadius: "8px",
          transition: "background 0.2s",
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#f0f0f0")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "none")}
      >
        <i className="bi bi-list" />
      </button>

      {/* Divider */}
      {!expanded && (
        <div
          style={{
            width: "60%",
            height: "1px",
            background: "#e0e0e0",
            margin: "0 0 16px 0",
            alignSelf: "center",
            borderRadius: "1px",
          }}
        />
      )}

      {/* Menu items */}
      <div style={{ width: "100%", marginTop: "8px", flex: 1 }}>
        {menuItems.map((item, idx) => {
          const isActive =
            currentPath === item.path ||
            (item.path !== "/dashboard" && currentPath.startsWith(item.path));
          const isHovered = hoveredIndex === idx;
          return (
            <button
              key={item.label}
              onClick={() => navigate(item.path)}
              className="sidebar-menu-btn"
              data-active={isActive ? "true" : undefined}
              onMouseEnter={() => setHoveredIndex(idx)}
              onMouseLeave={() => setHoveredIndex(null)}
              style={{
                width: "100%",
                display: "flex",
                flexDirection: expanded ? "row" : "column",
                alignItems: "center",
                justifyContent: expanded ? "flex-start" : "center",
                textAlign: expanded ? "left" : "center",
                gap: expanded ? "18px" : "4px",
                background: isActive
                  ? "rgba(0, 200, 83, 0.12)"
                  : isHovered
                    ? "#f5f5f5"
                    : "none",
                border: "none",
                outline: "none",
                padding: expanded ? "12px 18px" : "12px 8px",
                 fontWeight: 600,
                 fontSize: expanded ? "16px" : "11px",
                 color: isActive ? "#184b18" : "#555",
                borderRadius: "12px",
                marginBottom: "4px",
                transition: "all 0.2s ease",
                cursor: "pointer",
              }}
              title={!expanded ? item.label : undefined}
            >
              {item.label === "Feedback" ? (
                <img
                  src={ChatBubble}
                  alt="Feedback"
                  style={{
                    width: "20px",
                    height: "20px",
                    flexShrink: 0,
                    filter: isActive
                      ? "invert(32%) sepia(98%) saturate(1943%) hue-rotate(127deg)"
                      : "none",
                  }}
                />
              ) : (
                <omk-icon
                  slot="nav-icon"
                  style={{
                    fontSize: "20px",
                    flexShrink: 0,
                    color: isActive ? "#184b18" : "#555",
                  }}
                >
                  {item.icon}
                </omk-icon>
              )}
              <span
                style={{
                  flex: expanded ? 1 : undefined,
                  minWidth: 0,
                  color: isActive ? "#184b18" : "#555",
                  fontSize: expanded ? "16px" : "10px",
                  fontWeight: 600,
                  lineHeight: expanded ? "normal" : "1.1",
                  textAlign: expanded ? "left" : "center",
                  marginTop: expanded ? 0 : "2px",
                }}
              >
                {item.label}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Mobile bottom navigation - Fully Responsive
function MobileBottomNav({ navigate }) {
  const menuItems = [
    { label: "Home", icon: "home", path: "/dashboard" },
    { label: "Products", icon: "diamond", path: "/dashboard/products" },
    { label: "Transactions", icon: "receipt", path: "/dashboard/transactions" },
    { label: "Profile", icon: "person", path: "/dashboard/profile" },
    { label: "Settings", icon: "settings", path: "/dashboard/settings" },
    { label: "Feedback", path: "/dashboard/feedback" },
  ];
  const currentPath = window.location.pathname;

  return (
    <nav
      className="mobile-bottom-nav"
      style={{
        position: "fixed",
        left: 0,
        bottom: 0,
        width: "100%",
        background: "#fff",
        borderTop: "1px solid #eee",
        zIndex: 3000,
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        height: "clamp(50px, 12vh, 60px)",
        boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
        padding: "0",
        backdropFilter: "blur(0px)",
      }}
    >
      {menuItems.map((item) => {
        const isActive =
          currentPath === item.path ||
          (item.path !== "/dashboard" && currentPath.startsWith(item.path));
        return (
          <button
            key={item.label}
            onClick={() => navigate(item.path)}
            className="mobile-bottom-nav-btn"
            style={{
              background: "none",
              border: "none",
              outline: "none",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              flex: 1,
              color: isActive ? "#00b050" : "#999",
              fontSize: "clamp(10px, 3vw, 12px)",
              padding: "8px 4px",
              height: "100%",
              cursor: "pointer",
              transition: "all 0.2s ease",
              borderRadius: 0,
            }}
            aria-label={item.label}
          >
            {item.label === "Feedback" ? (
              <img
                src={ChatBubble}
                alt="Feedback"
                style={{
                  width: "clamp(20px, 6vw, 24px)",
                  height: "clamp(20px, 6vw, 24px)",
                  marginBottom: "2px",
                  filter: isActive
                    ? "brightness(0) saturate(100%) invert(32%) sepia(98%) saturate(1943%) hue-rotate(127deg) brightness(96%) contrast(105%)"
                    : "brightness(0) saturate(100%) invert(58%) sepia(11%) saturate(11%) hue-rotate(22deg) brightness(91%) contrast(87%)",
                }}
              />
            ) : (
              <omk-icon
                slot="nav-icon"
                style={{
                  fontSize: "clamp(20px, 6vw, 24px)",
                  color: isActive ? "#00b050" : "#999",
                  marginBottom: "2px",
                }}
              >
                {item.icon}
              </omk-icon>
            )}
          </button>
        );
      })}

      <style>{`
        .mobile-bottom-nav {
          box-shadow: 0 -2px 12px rgba(0,0,0,0.08);
        }
        .mobile-bottom-nav-btn:active {
          background: rgba(0, 176, 80, 0.1);
          transform: scale(0.95);
        }
        @media (min-width: 901px) {
          .mobile-bottom-nav {
            display: none !important;
          }
        }
        @media (max-width: 360px) {
          .mobile-bottom-nav-btn {
            padding: 6px 2px !important;
          }
        }
      `}</style>
    </nav>
  );
}
